import {Component} from '@angular/core';
import {FormBuilder, FormControl, FormGroup, Validators} from '@angular/forms';
import {CommunitiesService} from '../../_services/communities.service';
import {AuthService} from '../../_services/auth.service';
import {CustomButtonToggleOption, GenericResponse} from '../../_types/shared';
import {Community, SubGroup} from '../../_types/communities';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {Segment} from '../../_types/segments';
import {ReportsService} from '../../_services/reports.service';
import {FileReport, NewReport, PowerBiToken, Report, ReportStatus} from '../../_types/reports';
import {combineLatest, EMPTY, finalize, map, merge, Observable, of, startWith, switchMap, take, tap} from 'rxjs';
import {removeNullUndefinedFields} from '../../_utils/remove-null-fields';
import {formatDateToYYYYMMDD} from '../../_utils/date';
import {SnackbarService} from '../../_services/snackbar.service';
import {FeedPost} from '../../_types/content';
import {ContentService} from '../../_services/content.service';
import {User} from '../../_types/user';
import {PollsService} from '../../_services/polls.service';
import {Poll} from '../../_types/polls';
import {ACTIVITY_TYPES, BI_TYPES, REPORT_TYPES, ReportType} from './const';
import {SegmentsService} from '../../_services/segments.service';
import {MatTableDataSource} from "@angular/material/table";

interface Statistic {
    label: string;
    total: number | null;
    new?: number;
    skip?: boolean;
}

interface CombinedStatistic extends NewReport {
    title: string;
    id: number;
    type: 'Engagement' | 'Other',
    statistics?: Statistic[];
    fileName?: string;
    fileUrl?: string;
    created_at: string | Date;
}

enum ManagementMode {
    Generate,
    Exist,
    Pending,
    BI
}

@Component({
    selector: 'app-reports',
    templateUrl: './reports.component.html',
    styleUrl: './reports.component.scss'
})
export class ReportsComponent {
    communities: Community[] = [];
    subgroups: SubGroup[] = [];
    segments: Segment[] = [];
    allSegments: Segment[] = [];
    engagementReports: Report[] = [];
    otherReports: Report[] = [];
    posts: FeedPost[] = [];
    users: User[] = [];
    polls: Poll[] = [];
    communitiesFilter: string | number = 'All';
    form: FormGroup = this.fb.group({
        mode: this.fb.control(ManagementMode.Generate),
        communityId: this.fb.control(''),
        subgroupId: this.fb.control({value: '', disabled: true}),
        postId: this.fb.control({value: '', disabled: true}),
        memberId: this.fb.control({value: '', disabled: true}),
        pollId: this.fb.control({value: '', disabled: true}),
        segmentId: this.fb.control(''),
        startDate: this.fb.control(null),
        endDate: this.fb.control(null),
        activityType: this.fb.control(''),
    });
    reportType: FormControl = this.fb.control('', [Validators.required]);
    modeOptions: CustomButtonToggleOption[] = [
        {label: 'Generate new', value: ManagementMode.Generate, icon: 'note_add'},
        {label: 'Generating', value: ManagementMode.Pending, icon: 'cached'},
        {label: 'Generated Reports', value: ManagementMode.Exist, icon: 'find_in_page'},
        {label: 'Generate PowerBI', value: ManagementMode.BI, icon: 'pie_chart'},
    ];

    displayColumns: string[] = ['task_name', 'date_created', 'status']
    combinedStatistics: CombinedStatistic[] = [];
    filteredCombinedStatistics: CombinedStatistic[] = [];
    powerBiTokens: Map<number, PowerBiToken> = new Map();
    loading: boolean = false;
    loadingPowerBi: boolean = false;
    segmentLoading: boolean = false;
    page: number = 1;
    isNextPageAllowed: boolean = false;
    isNextDisabled: boolean = false;
    pendingReports: MatTableDataSource<any> = new MatTableDataSource();
    protected readonly managementMode: typeof ManagementMode = ManagementMode;
    protected reportTypes = REPORT_TYPES;
    protected readonly activityTypes: CustomButtonToggleOption[] = ACTIVITY_TYPES;
    private isAdmin: boolean = this.authService.isSuperUser();

    constructor(
        private readonly fb: FormBuilder,
        private readonly communitiesService: CommunitiesService,
        private readonly contentService: ContentService,
        private readonly authService: AuthService,
        private readonly reportsService: ReportsService,
        private readonly pollsService: PollsService,
        private readonly segmentsService: SegmentsService,
        private readonly snackbarService: SnackbarService,
    ) {
        if (this.isAdmin) {
            this.communitiesService.getAllCommunities().pipe(takeUntilDestroyed()).subscribe(data => {
                this.communities = data;
            })
        } else {
            this.communitiesService.getUserCommunities().pipe(takeUntilDestroyed()).subscribe(data => {
                this.communities = data.filter(result => result.is_moderator).map(result => result.community);
            })
        }

        this.form.get('mode')!.valueChanges
            .pipe(
                startWith(ManagementMode.Generate),
                tap((mode: ManagementMode): void => {
                    if (mode == ManagementMode.BI) {
                        this.reportTypes = BI_TYPES;
                        this.form.get('communityId')?.addValidators(Validators.required);
                        this.form.get('communityId')?.updateValueAndValidity();
                    } else {
                        this.reportTypes = REPORT_TYPES;
                        this.form.get('communityId')?.clearValidators();
                        this.form.get('communityId')?.updateValueAndValidity();
                    }
                    if (mode === ManagementMode.Exist) {
                        this.reloadContent();
                    } else if (mode === ManagementMode.Pending) {
                        this.getPendingReports();
                    }
                }),
                takeUntilDestroyed()
            ).subscribe();

        this.form.get('communityId')?.valueChanges
            .pipe(
                startWith(null),
                switchMap((res) => {
                    this.form.get('segmentId')?.reset('');
                    if (res) {
                        this.form.get('subgroupId')?.enable();
                        this.form.get('postId')?.enable();
                        this.form.get('memberId')?.enable();
                        this.form.get('pollId')?.enable();
                    } else {
                        this.form.get('subgroupId')?.disable();
                        this.form.get('postId')?.disable();
                        this.form.get('memberId')?.disable();
                        this.form.get('pollId')?.disable();
                    }

                    return res ? merge(this.getSubgroups(res), this.getPosts(res), this.getMembers(res), this.getPolls(), this.getSegments()) : this.getSegments();
                }),
                takeUntilDestroyed()
            ).subscribe();

        this.reportType.valueChanges.pipe(takeUntilDestroyed())
            .subscribe((): void => {
                this.form.get('communityId')?.reset();
                this.form.get('subgroupId')?.reset();
                this.form.get('postId')?.reset();
                this.form.get('memberId')?.reset();
                this.form.get('pollId')?.reset();
                this.form.get('segmentId')?.reset();
                this.form.get('startDate')?.reset();
                this.form.get('endDate')?.reset();
                this.form.get('activityType')?.reset();
            })
    }

    get reportControlIds(): string[] {
        return this.reportTypes.find(type => type.type === this.reportType.value)?.controls || [];
    }

    get selectedCommunityId(): number {
        return this.form.get('communityId')?.value;
    }

    get dateSet(): Community | null {
        return this.form.get('startDate')?.value || this.form.get('endDate')?.value;
    }

    get allowLoadMore(): boolean {
        return !this.isNextDisabled && !this.loading && this.isNextPageAllowed;
    }

    get selectedManageMode(): ManagementMode {
        return this.form.get('mode')?.value;
    }

    get selectedReportType(): ReportType {
        return this.reportType.value;
    }

    get selectedReportLabel(): string {
        return this.reportTypes.find(type => type.type === this.reportType.value)?.label || '';
    }

    allowGeneratePowerBi(data: CombinedStatistic): boolean {
        return (!!data.community_id || !!data.subgroup_id || !!data.segment_id) && !this.powerBiTokens.has(data.id);
    }

    clearDate(): void {
        this.form.get('startDate')?.reset(null);
        this.form.get('endDate')?.reset(null);
    }

    calculateNewPercentage(newValue: number, total: number): number {
        return (newValue / (total || 1)) * 100;
    }

    generate(powerbi?: boolean): void {
        this.loading = true;
        const data: NewReport = {
            community_id: this.form.value.communityId,
            segment_id: this.form.value.segmentId,
            subgroup_id: this.form.value.subgroupId,
            start_date: formatDateToYYYYMMDD(this.form.value.startDate),
            end_date: formatDateToYYYYMMDD(this.form.value.endDate),

            post_id: this.form.value.postId,
            member_id: this.form.value.memberId,
            poll_id: this.form.value.pollId,
            activity_type: this.form.value.activityType,

            powerbi: powerbi
        };

        if (this.selectedReportType === 'engagement' && (data.segment_id || data.subgroup_id)) {
            delete data['community_id'];
        }
        const payload: NewReport = removeNullUndefinedFields(data);
        this.startGenerateReport(payload, this.selectedReportType).subscribe({
            next: (res): void => {

                if (powerbi && res.data.url) {
                    navigator.clipboard.writeText(res.data.url);
                    this.snackbarService.openSuccessSnackBar(`${this.selectedReportLabel} Bi URL has been copied to your clipboard. The first report may some time to generate after creating a PowerBI URL`, 20000);
                } else {
                    this.reportsService.triggerListenForReportStatus(res.data.task_id);
                    this.snackbarService.openSuccessSnackBar(`${this.selectedReportLabel} Report is being generated, this may take up to 10 minutes. Feel free to leave this page, an email will be sent to you once this is complete.`, 2000);
                }

                this.form.reset();
                this.form.get('mode')?.setValue(ManagementMode.Generate);
                this.loading = false;
            },
            error: (err): void => {
                this.loading = false;
                this.snackbarService.openErrorSnackBar(err);
            }
        });
    }

    reloadContent(freshLoad: boolean = true): void {
        this.engagementReports = [];
        this.otherReports = [];
        this.page = 1;
        if (freshLoad) {
            this.communitiesFilter = 'All';
        }
        this.loadAllReports(1);

    }

    loadMore(): void {
        if (!this.isNextPageAllowed || this.loading) return;
        this.loadAllReports(this.page + 1);
        this.page += 1;
    }

    generatePowerBi(stats: CombinedStatistic): void {
        if (this.loading || this.loadingPowerBi || this.powerBiTokens.has(stats.id)) return;
        this.loadingPowerBi = true;

        const data: NewReport = {
            community_id: stats.community_id,
            segment_id: stats.segment_id,
            subgroup_id: stats.subgroup_id,
            start_date: stats.start_date,
            end_date: stats.end_date,
        };
        const payload: NewReport = removeNullUndefinedFields(data);
        this.reportsService.generatePowerBiReport(payload).pipe(take(1))
            .subscribe({
                next: (res): void => {
                    this.powerBiTokens.set(stats.id, res.data);
                    this.snackbarService.openSuccessSnackBar('Report successfully generated!')
                    this.loadingPowerBi = false;
                },
                error: (err): void => {
                    this.loadingPowerBi = false;
                    this.snackbarService.openErrorSnackBar(err);
                }
            });
    }

    userName(user: User): string {
        const fName: string = user?.first_name || '';
        const lName: string = user?.last_name || '';
        const fullName: string = `${fName} ${lName}`.trim();
        return fullName || user?.username;
    }

    public loadAllReports(page: number): void {
        if (this.loading) return;
        this.loading = true;
        this.loadReports(page)
            .pipe(take(1)).subscribe({
            next: ([firstResponse, secondResponse]): void => {
                this.engagementReports = this.engagementReports.concat(firstResponse.results);
                this.otherReports = this.otherReports.concat(secondResponse.results);
                this.combinedStatistics = this.createCombinedStatisticsArray(this.engagementReports).concat(this.createFileStatisticsArray(this.otherReports as any))
                    .sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());
                this.filteredCombinedStatistics = this.combinedStatistics;

            },
            complete: (): void => {
                this.loading = false;
            },
            error: (err): void => {
                this.loading = false;
                console.error('Error loading reports:', err);
            }
        });
    }

    downloadFile(statSet: CombinedStatistic) {
        this.contentService.downloadCSV(statSet.fileUrl!, statSet.fileName!)
    }

    getCommunityFromId(id: number): string {
        const i: number = this.communities.findIndex((x: any): boolean => x.id === id);
        return i != null && i >= 0 ? this.communities[i].name : '';
    }

    private getSubgroups(id: number): Observable<SubGroup[]> {
        this.subgroups = [];
        return this.communitiesService.getSubGroups(id)
            .pipe(
                map(res => res || []),
                tap(res => this.subgroups = res)
            );
    }

    private getPosts(id: number): Observable<FeedPost[]> {
        this.posts = [];
        return this.contentService.getFeed(id, 1, 200)
            .pipe(
                map(res => res.results || []),
                tap(res => this.posts = res)
            );
    }

    private getMembers(id: number): Observable<User[]> {
        this.users = [];
        // TODO replace with searchUserList
        return this.communitiesService.getUserList(id)
            .pipe(
                map(res => res.map(usr => usr.user) || []),
                tap(res => this.users = res)
            );
    }

    private getPolls(): Observable<Poll[]> {
        this.users = [];
        return this.pollsService.getAllPolls(this.selectedCommunityId || undefined, true)
            .pipe(
                map(res => res || []),
                tap(res => this.polls = res)
            );
    }

    private getSegments(): Observable<Segment[]> {
        this.segments = [];
        this.segmentLoading = true;

        // as the get segments is called every time the form is reset check if all segments cache exists first
        if (!this.selectedCommunityId && this.allSegments.length > 0) {
            this.segments = this.allSegments
            this.segmentLoading = false;
            return of(this.segments);
        }
        return this.segmentsService.getSegments(this.selectedCommunityId || undefined)
            .pipe(
                map(res => res || []),
                tap(res => {
                    if (!this.selectedCommunityId) {
                        // Cache the call to all segments
                        this.allSegments = res;
                    }
                    this.segments = res;
                }),
                finalize((): void => {
                    this.segmentLoading = false; // Set loading to false when observable completes
                })
            );
    }

    private createCombinedStatisticsArray(statsArray: Report[]): CombinedStatistic[] {
        if (statsArray.length > 0) {
            return statsArray.map(stats => {
                const labelsMap = {
                    // Add section title
                    comments_section: {label: '--- Comments ---', skip: true},

                    // Group: Comments
                    commented_members: 'Members who have commented',
                    comments: 'Total member comments',
                    likes_on_comments: 'Likes on Comments',
                    dislikes_on_comments: 'Dislikes on Comments',
                    members_liked_comments: 'Members who have liked a comment',
                    members_disliked_comments: 'Members who have disliked a comment',
                    total_number_comment_likes: 'Total member comment likes',
                    total_number_comment_dislikes: 'Total member comment dislikes',

                    // Add section title
                    posts_section: {label: '--- Posts ---', skip: true},

                    // Group: Posts
                    likes_on_posts: 'Member Likes on Posts',
                    dislikes_on_posts: 'Member Dislikes on Posts',
                    members_liked_posts: 'Members who have liked a post',
                    members_disliked_posts: 'Members who have disliked a post',
                    posts_created: 'Member created posts',
                    total_number_post_likes: 'Total member post likes',
                    total_number_post_dislikes: 'Total member post dislikes',

                    // Add section title
                    polls_section: {label: '--- Polls ---', skip: true},

                    // Group: Polls
                    poll_commented_members: 'Members who have commented on a poll',
                    poll_voted_members: 'Members who have voted in a poll',
                    poll_votes: 'Total member poll votes',

                    // Add section title
                    memberships_section: {label: '--- Memberships ---', skip: true},

                    // Group: Memberships
                    logged_in_members: 'Members who have logged in',
                    new_memberships: 'New membership',
                    total_memberships: 'Total membership',
                    participated_members: 'Unique participation',
                    lefted_community_members_in_period: 'Members that have left',

                    // Add section title
                    surveys_section: {label: '--- Surveys ---', skip: true},

                    // Group: Surveys
                    completed_survey_members: 'Survey Completed Members',
                    opened_survey_members: 'Members that have opened a survey',

                    // Add section title
                    discussion_section: {label: '--- Discussion ---', skip: true},

                    // Group: Discussion
                    discussion_authors: 'Members who have created a discussion',

                    // Add section title
                    totals_section: {label: '--- Totals ---', skip: true},

                    // Group: Totals
                    total_number_likes: 'Total Number of Likes',
                    total_number_dislikes: 'Total Number of Dislikes',
                    total_number_likes_dislikes: 'Total Number of Likes & Dislikes',
                };

                const combinedStats: Statistic[] = [];

                for (const [key, labelConfig] of Object.entries(labelsMap)) {
                    if (typeof labelConfig === 'object' && labelConfig.skip) {
                        // Add the section divider as a placeholder
                        combinedStats.push({label: labelConfig.label, total: null, skip: true});
                    } else if (stats.report_data) {
                        // Use data from `stats.report_data` or default to 0
                        const value = (stats.report_data as any)[key] ?? 0;
                        combinedStats.push({label: labelConfig as string, total: value});
                    }
                }

                return {
                    title: stats.title,
                    id: stats.id,
                    community_id: stats.community,
                    subgroup_id: stats.subgroup,
                    segment_id: stats.segment,
                    start_date: stats.start_date,
                    end_date: stats.end_date,
                    statistics: combinedStats,
                    type: 'Engagement',
                    created_at: stats.created_at
                };
            });
        } else {
            return [];
        }
    }

    private createFileStatisticsArray(statsArray: FileReport[]): CombinedStatistic[] {
        return statsArray.map(stats => {


            return {
                title: stats.title,
                id: stats.id,
                community_id: stats.community,
                subgroup_id: stats.subgroup,
                segment_id: stats.segment,
                start_date: stats.start_date,
                end_date: stats.end_date,
                fileName: stats.file_name,
                fileUrl: stats.file_url,
                type: 'Other',
                created_at: stats.created_at
            };
        });
    }

    private startGenerateReport(payload: NewReport, selectedReportType: ReportType): Observable<GenericResponse<ReportStatus>> {
        switch (selectedReportType) {
            case ReportType.ENGAGEMENT:
                return this.reportsService.generateEngagementReport(payload).pipe(take(1));
            case ReportType.COMMENTS:
                return this.reportsService.generateCommentsReport(payload).pipe(take(1));
            case ReportType.MEMBERS:
                return this.reportsService.generateMembersReport(payload).pipe(take(1));
            case ReportType.MEMBERS_SEGMENTATION:
                return this.reportsService.generateMembersSegmentationReport(payload).pipe(take(1));
            case ReportType.POLL_VOTES:
                return this.reportsService.generatePollVotesReport(payload).pipe(take(1));
            case ReportType.RAW_ACTIVITY_LOG:
                return this.reportsService.generateRawActivityLogReport(payload).pipe(take(1));
            case ReportType.RAW_POLLS_RESPONSES:
                return this.reportsService.generateRawPollsResponsesReport(payload).pipe(take(1));
            case ReportType.SUPPRESSION:
                return this.reportsService.generateSuppressionReport(payload).pipe(take(1));
            default:
                return EMPTY
        }
    }

    private loadReports(page: number) {
        // Make the API calls in parallel
        const firstApiCall$ = this.reportsService.getEngagementReports(page, 10, this.communitiesFilter);
        const secondApiCall$ = this.reportsService.getCommunitiesReports(page, 10, this.communitiesFilter);

        return combineLatest([firstApiCall$, secondApiCall$]).pipe(
            tap(([firstResponse, secondResponse]) => {
                this.isNextDisabled = firstResponse.next === null && secondResponse.next === null;

                if (firstResponse.next === null) {
                    this.isNextDisabled = true;
                }

                if (secondResponse.next === null) {
                    this.isNextDisabled = true;
                }
            })
        );
    }

    private getPendingReports(): void {
        this.reportsService.getPendingReports().subscribe(reports => {
            this.pendingReports.data = reports.results;
        }, e => {
            this.snackbarService.openErrorSnackBar(e);
        })
    }
}
